import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  ExternalLink,
  MessageSquare,
  Phone,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { formatPhoneNumber } from '@/components/audio-conversation/utils';
import { Button } from '@/components/ui/button';
import { isTreatmentSmsEnabledForUser } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import type { TreatmentContactQueueItem } from '@/services/api/generated/treatmentContactQueueService';
import { selectEmail } from '@/state/selectors/root-selectors';

import { UrgencyBadge } from './UrgencyBadge';

interface ContactQueueItemProps {
  item: TreatmentContactQueueItem;
  onSendSms: (item: TreatmentContactQueueItem) => void;
}

export const ContactQueueItem = ({
  item,
  onSendSms,
}: ContactQueueItemProps) => {
  const userEmail = useSelector(selectEmail);
  const isSmsEnabled = isTreatmentSmsEnabledForUser(userEmail);

  const urgencyBorderColors = {
    'Escalate to Attorney': 'border-l-red-500',
    Urgent: 'border-l-orange-500',
    Overdue: 'border-l-yellow-500',
    Routine: 'border-l-zinc-300',
  };

  const getDaysSinceText = () => {
    if (
      item.days_since_last_response != null &&
      item.days_since_last_response > 0
    ) {
      return `${item.days_since_last_response} days no response`;
    }
    if (item.days_since_last_contact != null) {
      return `${item.days_since_last_contact} days since contact`;
    }
    if (item.has_overdue_check_in_task && item.treatment_check_in_task) {
      return 'Task overdue';
    }
    return null;
  };

  const getLastContactText = () => {
    if (item.last_contacted_at) {
      return `Last contacted: ${formatDistanceToNow(new Date(item.last_contacted_at), { addSuffix: true })}`;
    }
    return 'No previous contact';
  };

  return (
    <div
      className={cn(
        'rounded-lg border bg-white p-4 shadow-sm transition-shadow hover:shadow-md',
        'border-l-4',
        urgencyBorderColors[item.urgency || 'Routine'],
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          {/* Header: Client name + urgency */}
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-zinc-900 truncate">
              {item.client_name}
            </h3>
            {item.urgency && <UrgencyBadge urgency={item.urgency} />}
            {getDaysSinceText() && (
              <span className="text-xs text-zinc-500">
                {getDaysSinceText()}
              </span>
            )}
          </div>

          {/* Matter link */}
          <div className="flex items-center gap-1.5">
            <Link
              to={`/matters/${item.matter_id}`}
              className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
            >
              {item.matter_name}
              <ExternalLink className="h-3 w-3" />
            </Link>
            <span className="text-xs text-zinc-400">•</span>
            <span className="text-xs text-zinc-500">{item.firm_name}</span>
          </div>

          {/* Providers */}
          {item.providers && item.providers.length > 0 && (
            <div className="text-xs text-zinc-600">
              <span className="text-zinc-500">Providers: </span>
              {item.providers.join(', ')}
            </div>
          )}

          {/* Phone and last contact */}
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            {item.client_phone && (
              <span className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {formatPhoneNumber(item.client_phone)}
              </span>
            )}
            <span>{getLastContactText()}</span>
          </div>

          {/* Escalation warning */}
          {item.requires_attorney_escalation && (
            <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" />
              Requires attorney escalation
            </div>
          )}
        </div>

        {/* Actions */}
        {isSmsEnabled && (
          <div className="flex-shrink-0">
            <Button
              variant="default"
              size="sm"
              onClick={() => onSendSms(item)}
              disabled={!item.client_phone}
              className="gap-1.5"
            >
              <MessageSquare className="h-4 w-4" />
              Send SMS
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
